import React, { useEffect, useState } from "react";
import TopBar from "../../components/TopBar";
import EmptyState from "../../components/EmptyState";
import { Modal, Field } from "../Fields";
import { api } from "../../lib/api";
import { Users, Plus, Pencil, Trash2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const blank = { name: "", company: "", email: "", phone: "", address: "", notes: "", preferred_business: "" };

export default function Customers({ title = "Customers", subtitle, preferredBusinessId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [search, setSearch] = useState("");

  const load = async () => {
    const { data } = await api.get(`/customers`);
    setRows(data);
  };

  useEffect(() => { load(); }, []);

  const filtered = !search ? rows : rows.filter((r) =>
    [r.name, r.company, r.email, r.phone].some((v) => (v || "").toLowerCase().includes(search.toLowerCase()))
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/customers/${editing.id}`, form);
      else await api.post(`/customers`, { ...form, preferred_business: form.preferred_business || preferredBusinessId || "" });
      toast.success(editing ? "Customer updated" : "Customer added");
      setOpen(false); setEditing(null); setForm(blank); load();
    } catch { toast.error("Could not save customer"); }
  };

  const onEdit = (r) => { setEditing(r); setForm({ ...blank, ...r }); setOpen(true); };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this customer? Any linked invoices/jobs will lose the link.")) return;
    await api.delete(`/customers/${id}`); toast.success("Customer deleted"); load();
  };

  return (
    <div>
      <TopBar
        title={title}
        subtitle={subtitle || "Shared customer book — same person can be a Repair customer and a Trucking customer."}
        actions={
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <button
              data-testid="add-customer-btn"
              onClick={() => { setEditing(null); setForm({ ...blank, preferred_business: preferredBusinessId || "" }); setOpen(true); }}
              className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#A03B1F] transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> New Customer
            </button>
          </div>
        }
      />

      <div className="bg-white border border-[#1F1E1B] p-4 mb-6">
        <input
          data-testid="customer-search"
          placeholder="Search by name, company, phone, email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Users} title="No customers yet" hint="Add your first customer to start sending invoices." testid="empty-customers" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="customers-grid">
          {filtered.map((c) => (
            <div key={c.id} className="bg-white border border-[#1F1E1B] p-4" data-testid={`customer-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-lg font-bold truncate">{c.name}</h3>
                  {c.company && <div className="text-sm text-[#66645E] truncate">{c.company}</div>}
                </div>
                <div className="flex gap-1.5 flex-shrink-0 ml-2">
                  <button data-testid={`edit-customer-${c.id}`} onClick={() => onEdit(c)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                  <button data-testid={`delete-customer-${c.id}`} onClick={() => onDelete(c.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
              <div className="mt-3 space-y-1 text-xs">
                {c.phone && <div className="flex items-center gap-2 text-[#66645E]"><Phone className="w-3 h-3" /> {c.phone}</div>}
                {c.email && <div className="flex items-center gap-2 text-[#66645E]"><Mail className="w-3 h-3" /> {c.email}</div>}
                {c.address && <div className="text-[#66645E] mt-1">{c.address}</div>}
              </div>
              {c.notes && <div className="text-xs text-[#66645E] italic mt-2 pt-2 border-t border-[#E5E2D6]">{c.notes}</div>}
            </div>
          ))}
        </div>
      )}

      {open && (
        <Modal title={editing ? "Edit Customer" : "New Customer"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="customer-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" required><input data-testid="customer-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
              <Field label="Company"><input data-testid="customer-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Phone"><input data-testid="customer-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
              <Field label="Email"><input data-testid="customer-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            </div>
            <Field label="Address"><textarea data-testid="customer-address-input" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            <Field label="Notes"><textarea data-testid="customer-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="customer-cancel-btn" onClick={() => setOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="customer-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
